// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/kritaTranslate.js
//
// Krita paint-op preset -> Voxemera brush.
//
// Krita curve options (KisCurveOption) are stored as a family of params:
//   Pressure<Name>      option enabled (checkable options only; Opacity and
//                       Flow are always on for the pixel engine)
//   <Name>Value         option strength / maximum
//   <Name>UseCurve      false = the sensors are ignored (constant value)
//   <Name>UseSameCurve  true = every sensor uses <Name>commonCurve instead
//                       of its own curve
//   <Name>curveMode     how several sensors combine: 0 multiply, 1 add,
//                       2 max, 3 min, 4 difference
//   <Name>Sensor        <params id="pressure"><curve>..</curve></params> or
//                       <params id="sensorslist"><ChildSensor id=".."/>..
// Curves are natural cubic splines (curves.js bakes them).
//
// Pixel brush ("paintbrush") translates to the stamp engine. Colour smudge
// keeps its tip and dynamics but is reported as partial because smudging
// and colour pickup are not simulated yet. Engines with no Voxemera
// equivalent are kept with their source and marked unsupported; they are
// never painted as a generic stamp.
import { bakeCurve, isIdentityCurve, parseKritaCurve } from './curves.js';
import { decodeXmlEntities, kritaParamBool, kritaParamNumber, kritaParamString, parseXmlAttributes } from './kritaPreset.js';
import { scoreCompatibility } from './brushSchema.js';

export const KRITA_ENGINE_LABELS = Object.freeze({
  paintbrush: 'Pixel brush',
  colorsmudge: 'Colour smudge',
  sketchbrush: 'Sketch',
  hatchingbrush: 'Hatching',
  spraybrush: 'Spray',
  particlebrush: 'Particle',
  hairybrush: 'Bristle',
  chalkbrush: 'Chalk',
  curvebrush: 'Curve',
  deformbrush: 'Deform',
  dynabrush: 'Dyna',
  experimentbrush: 'Shape (experiment)',
  filter: 'Filter',
  gridbrush: 'Grid',
  duplicate: 'Clone',
  roundmarker: 'Quick brush',
  tangentnormal: 'Tangent normal',
  mypaintbrush: 'MyPaint',
});

// Why each non-stamp engine cannot be faithfully reproduced today. Shown in
// the importer so the user knows what was kept and why.
const UNSUPPORTED_ENGINE_REASONS = Object.freeze({
  sketchbrush: 'draws connecting lines to earlier stroke points; needs a dedicated line-network engine',
  hatchingbrush: 'generates parallel hatch lines inside the dab; needs a procedural hatching engine',
  spraybrush: 'emits particle clouds with per-particle shapes; needs a particle spray engine',
  particlebrush: 'simulates particle trajectories with gravity; needs a particle engine',
  hairybrush: 'simulates individual bristles with ink depletion; needs a bristle engine',
  chalkbrush: 'random-pixel chalk engine',
  curvebrush: 'draws curves between stroke history points',
  deformbrush: 'deforms existing pixels rather than painting',
  dynabrush: 'physics-driven dynamic line drawing',
  experimentbrush: 'fills the enclosed stroke shape',
  filter: 'applies a filter under the brush',
  gridbrush: 'paints a fixed grid of cells',
  duplicate: 'clones pixels from a source point',
  roundmarker: 'Krita quick-brush engine with a separate size/spacing model',
  tangentnormal: 'paints tangent-space normal colours from tilt',
  mypaintbrush: 'MyPaint engine inside Krita; import the .myb file instead',
});

const SENSOR_LABELS = {
  pressure: 'pressure',
  pressurein: 'pressure in',
  xtilt: 'x tilt',
  ytilt: 'y tilt',
  ascension: 'tilt direction',
  declination: 'tilt elevation',
  tiltdirection: 'tilt direction',
  tiltelevation: 'tilt elevation',
  speed: 'speed',
  drawingangle: 'drawing angle',
  rotation: 'pen rotation',
  distance: 'distance',
  time: 'time',
  fuzzy: 'fuzzy dab',
  fuzzystroke: 'fuzzy stroke',
  fade: 'fade',
  perspective: 'perspective',
  tangentialpressure: 'tangential pressure',
};

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function parseKritaSensors(sensorXml) {
  const xml = decodeXmlEntities(String(sensorXml || ''));
  const sensors = [];
  const childRegex = /<ChildSensor\b([^>]*)>([\s\S]*?)<\/ChildSensor>|<ChildSensor\b([^>]*)\/>/gi;
  let match;
  let sawChild = false;
  while ((match = childRegex.exec(xml))) {
    sawChild = true;
    const attributes = parseXmlAttributes(match[1] || match[3] || '');
    const curve = ((match[2] || '').match(/<curve>([\s\S]*?)<\/curve>/i) || [])[1] || '';
    sensors.push({ id: String(attributes.id || '').toLowerCase(), curve: parseKritaCurve(curve), attributes });
  }
  if (!sawChild) {
    const root = xml.match(/<params\b([^>]*)>([\s\S]*?)<\/params>/i);
    if (root) {
      const attributes = parseXmlAttributes(root[1]);
      const id = String(attributes.id || '').toLowerCase();
      if (id && id !== 'sensorslist') {
        const curve = (root[2].match(/<curve>([\s\S]*?)<\/curve>/i) || [])[1] || '';
        sensors.push({ id, curve: parseKritaCurve(curve), attributes });
      }
    }
  }
  return sensors;
}

export function readKritaCurveOption(preset, name, { checkable = true } = {}) {
  const hasValue = preset.params && (name + 'Value') in preset.params;
  const enabled = checkable ? kritaParamBool(preset, 'Pressure' + name, false) : hasValue || !!(preset.params && preset.params[name + 'Sensor']);
  const value = kritaParamNumber(preset, name + 'Value', 1);
  const useCurve = kritaParamBool(preset, name + 'UseCurve', true);
  const useSameCurve = kritaParamBool(preset, name + 'UseSameCurve', true);
  const curveMode = Math.round(kritaParamNumber(preset, name + 'curveMode', 0));
  const commonCurve = parseKritaCurve(kritaParamString(preset, name + 'commonCurve', ''));
  const sensors = parseKritaSensors(kritaParamString(preset, name + 'Sensor', '')).map((sensor) => ({
    ...sensor,
    curve: useSameCurve && commonCurve.length >= 2 ? commonCurve : sensor.curve,
  }));
  return { name, enabled, value, useCurve, useSameCurve, curveMode, commonCurve, sensors };
}

function bakedSensorCurve(sensor) {
  if (!sensor || sensor.curve.length < 2 || isIdentityCurve(sensor.curve)) return [];
  return bakeCurve(sensor.curve, 'krita');
}

// Converts a Krita option into a Voxemera channel and records what was
// approximated or missing.
function translateChannel(option, label, report) {
  const channel = { pressure: { amount: 0, curve: [] }, tilt: { amount: 0, angle: 0 }, speed: 0, jitter: 0, minimum: 0 };
  if (!option.enabled || !option.useCurve) return channel;
  const used = [];
  option.sensors.forEach((sensor) => {
    const id = sensor.id;
    if (id === 'pressure' || id === 'pressurein') {
      channel.pressure = { amount: 1, curve: bakedSensorCurve(sensor) };
      used.push('pressure');
      if (id === 'pressurein') report.approximated.push(`${label}: "pressure in" treated as pressure`);
    } else if (id === 'fuzzy') {
      channel.jitter = 1;
      used.push('fuzzy');
      if (sensor.curve.length && !isIdentityCurve(sensor.curve)) report.approximated.push(`${label}: fuzzy curve shape`);
    } else if (id === 'speed') {
      channel.speed = -1;
      used.push('speed');
      report.approximated.push(`${label}: speed sensor`);
    } else if (id === 'declination' || id === 'tiltelevation' || id === 'xtilt' || id === 'ytilt') {
      channel.tilt = { amount: 1, angle: 0 };
      used.push('tilt');
      report.approximated.push(`${label}: tilt sensor`);
    } else if (id) {
      report.missing.push(`${label}: ${SENSOR_LABELS[id] || id} sensor`);
    }
  });
  if (used.length > 1 && option.curveMode !== 0) report.approximated.push(`${label}: sensor combine mode ${option.curveMode} treated as multiply`);
  if (used.includes('pressure')) report.implemented.push(`pressure → ${label} (curve)`);
  return channel;
}

const KRITA_COMPOSITE_TO_SUPPORT = { normal: 'normal', over: 'normal', erase: 'erase' };

export function translateKritaPreset(preset, context = {}) {
  const report = { implemented: [], approximated: [], missing: [], notes: [] };
  const paintop = String(preset.paintopid || '').toLowerCase();
  const engineLabel = KRITA_ENGINE_LABELS[paintop] || paintop || 'unknown';
  const brushDefinition = decodeXmlEntities(kritaParamString(preset, 'brush_definition', ''));
  const brushTag = (brushDefinition.match(/<Brush\b[^>]*>/i) || [''])[0];
  const brush = parseXmlAttributes(brushTag);
  const maskTag = (brushDefinition.match(/<MaskGenerator\b[^>]*>/i) || [''])[0];
  const mask = maskTag ? parseXmlAttributes(maskTag) : null;
  const brushType = String(brush.type || '').toLowerCase();

  const stampEngines = ['paintbrush', 'colorsmudge'];
  const renderable = stampEngines.includes(paintop) && ['auto_brush', 'gbr_brush', 'png_brush', 'svg_brush', 'abr_brush'].includes(brushType || 'auto_brush');

  if (!stampEngines.includes(paintop)) {
    report.notes.push(`${engineLabel} engine: ${UNSUPPORTED_ENGINE_REASONS[paintop] || 'no Voxemera equivalent yet'}. The original preset is kept for a future engine.`);
    return {
      engine: { id: 'unsupported', params: { paintop } },
      sourceEngine: 'krita.' + (paintop || 'unknown'),
      engineLabel,
      brush,
      mask,
      settings: { spacing: 0.1, opacity: 1, flow: 1 },
      dynamics: {},
      texture: null,
      rendering: { mode: 'glaze' },
      compatibility: scoreCompatibility({ ...report, unrenderable: true }),
    };
  }

  if (!renderable) {
    report.notes.push(`Brush tip type "${brushType}" cannot be rendered yet (text brushes are not supported).`);
  }

  report.implemented.push(brushType === 'auto_brush' ? 'auto brush tip (mask generator)' : 'predefined brush tip');
  if (paintop === 'colorsmudge') {
    report.missing.push({ label: 'colour smudge: smearing, colour pickup and paint thickness are not simulated (paints the tip colour only)', major: true });
  }

  const sizeOption = readKritaCurveOption(preset, 'Size');
  const opacityOption = readKritaCurveOption(preset, 'Opacity', { checkable: false });
  const flowOption = readKritaCurveOption(preset, 'Flow', { checkable: false });
  const spacingOption = readKritaCurveOption(preset, 'Spacing');
  const rotationOption = readKritaCurveOption(preset, 'Rotation');
  const scatterOption = readKritaCurveOption(preset, 'Scatter');

  const sizeChannel = translateChannel(sizeOption, 'size', report);
  const opacityChannel = translateChannel(opacityOption, 'opacity', report);
  const flowChannel = translateChannel(flowOption, 'flow', report);
  const spacingChannel = translateChannel(spacingOption, 'spacing', report);

  const rotation = { follow: 0, jitter: 0, randomPerStroke: false, azimuth: false };
  let angle = clamp(Number(brush.angle) || 0, -Math.PI * 4, Math.PI * 4);
  if (rotationOption.enabled && rotationOption.useCurve) {
    rotationOption.sensors.forEach((sensor) => {
      if (sensor.id === 'drawingangle') {
        rotation.follow = 1;
        const offset = Number(sensor.attributes.angleOffset) || 0;
        angle += (offset * Math.PI) / 180;
        report.implemented.push('rotation follows drawing angle');
        if (sensor.attributes.lockedAngleMode === '1' || sensor.attributes.fanCornersEnabled === '1') report.approximated.push('drawing angle lock/fan corners');
      } else if (sensor.id === 'fuzzy' || sensor.id === 'fuzzystroke') {
        rotation.jitter = Math.PI * 2 * clamp(rotationOption.value, 0, 1);
        if (sensor.id === 'fuzzystroke') rotation.randomPerStroke = true;
        report.implemented.push('random rotation');
      } else if (sensor.id === 'ascension' || sensor.id === 'tiltdirection') {
        rotation.azimuth = true;
        report.implemented.push('rotation follows tilt direction');
      } else if (sensor.id === 'rotation') {
        rotation.azimuth = true;
        report.approximated.push('pen barrel rotation mapped to tilt direction');
      } else if (sensor.id) {
        report.missing.push(`rotation: ${SENSOR_LABELS[sensor.id] || sensor.id} sensor`);
      }
    });
  }

  let scatter = { perpendicular: 0, along: 0, pressure: false };
  if (scatterOption.enabled) {
    const amount = clamp(Number(scatterOption.value) || 0, 0, 8);
    const axisX = kritaParamBool(preset, 'Scattering/AxisX', true);
    const axisY = kritaParamBool(preset, 'Scattering/AxisY', true);
    const pressureDriven = scatterOption.useCurve && scatterOption.sensors.some((sensor) => sensor.id === 'pressure');
    scatter = { perpendicular: axisY ? amount : 0, along: axisX ? amount : 0, pressure: pressureDriven };
    report.implemented.push('scatter');
  }

  const rawSpacing = Number(brush.spacing);
  let spacing = Number.isFinite(rawSpacing) && rawSpacing > 0 ? rawSpacing : 0.1;
  if (String(brush.useAutoSpacing) === '1' || String(brush.useAutoSpacing).toLowerCase() === 'true') {
    report.approximated.push('auto spacing evaluated at a 40 px reference size');
    const coefficient = Number(brush.autoSpacingCoeff) || 1;
    spacing = clamp((coefficient * Math.sqrt(40)) / 40, 0.01, 2);
  }
  report.implemented.push('spacing');

  const opacity = clamp(kritaParamNumber(preset, 'OpacityValue', 1), 0.01, 1);
  const flow = clamp(kritaParamNumber(preset, 'FlowValue', 1), 0.005, 1);
  const paintAction = Math.round(kritaParamNumber(preset, 'PaintOpAction', 1));
  const wash = paintAction === 2;
  report.implemented.push(wash ? 'wash painting mode (stroke opacity cap)' : 'build-up painting mode');

  const composite = kritaParamString(preset, 'CompositeOp', 'normal').toLowerCase();
  const eraser = composite === 'erase' || kritaParamBool(preset, 'EraserMode', false);
  if (!KRITA_COMPOSITE_TO_SUPPORT[composite]) report.missing.push(`blending mode "${composite}"`);
  if (eraser) report.implemented.push('eraser');

  const sharpness = readKritaCurveOption(preset, 'Sharpness');
  const edgeMode = sharpness.enabled && brushType !== 'auto_brush' ? 'hard' : 'normal';
  if (sharpness.enabled) report.approximated.push('sharpness → hard edge threshold');
  if (readKritaCurveOption(preset, 'Softness').enabled && brushType === 'auto_brush') report.missing.push('softness option');
  if (readKritaCurveOption(preset, 'Ratio').enabled) report.missing.push('ratio option');
  if (readKritaCurveOption(preset, 'Mirror').enabled) report.missing.push('mirror option');
  if (readKritaCurveOption(preset, 'Darken').enabled) report.missing.push('darken option');
  if (readKritaCurveOption(preset, 'Mix').enabled && kritaParamString(preset, 'ColorSource/Type', 'plain') !== 'plain') report.missing.push('mix option');

  const colour = { perDab: { hue: 0, saturation: 0, lightness: 0, darkness: 0 }, perStroke: { hue: 0, saturation: 0, lightness: 0, darkness: 0 } };
  [['h', 'hue'], ['s', 'saturation'], ['v', 'lightness']].forEach(([key, target]) => {
    const option = readKritaCurveOption(preset, key);
    if (!option.enabled) return;
    const fuzzy = option.sensors.find((sensor) => sensor.id === 'fuzzy' || sensor.id === 'fuzzystroke');
    if (fuzzy) {
      (fuzzy.id === 'fuzzy' ? colour.perDab : colour.perStroke)[target] = clamp(Math.abs(option.value), 0, 1);
      report.approximated.push(`${target} colour jitter`);
    } else {
      report.missing.push(`${target} colour option (non-random sensors)`);
    }
  });
  const colourSource = kritaParamString(preset, 'ColorSource/Type', 'plain');
  if (colourSource !== 'plain') report.missing.push(`colour source "${colourSource}"`);

  let texture = null;
  if (kritaParamBool(preset, 'Texture/Pattern/Enabled', false)) {
    const textureMode = Math.round(kritaParamNumber(preset, 'Texture/TexturingMode', kritaParamNumber(preset, 'Texture/Pattern/TexturingMode', 0)));
    const strengthOption = readKritaCurveOption(preset, 'Texture/Strength/');
    texture = {
      hash: context.patternHash || '',
      patternFileName: kritaParamString(preset, 'Texture/Pattern/PatternFileName', '') || kritaParamString(preset, 'Texture/Pattern/Name', ''),
      patternMd5: kritaParamString(preset, 'Texture/Pattern/PatternMD5', ''),
      mode: 'texturized',
      movement: 0,
      scale: clamp(kritaParamNumber(preset, 'Texture/Pattern/Scale', 1), 0.02, 16),
      depth: clamp(strengthOption.value, 0, 1),
      depthMinimum: 0,
      // Krita masks with the pattern's darkness (narrowing the cutoff to the
      // left keeps "only the darkest values" drawn, docs.krita.org Texture);
      // the Voxemera shader treats light as paint, so the flag is flipped.
      invert: !kritaParamBool(preset, 'Texture/Pattern/Invert', false),
      embeddedPattern: kritaParamString(preset, 'Texture/Pattern/Pattern', '').length < 24 * 1024 * 1024 ? kritaParamString(preset, 'Texture/Pattern/Pattern', '') : '',
      brightness: clamp(kritaParamNumber(preset, 'Texture/Pattern/Brightness', 0), -1, 1),
      contrast: clamp(kritaParamNumber(preset, 'Texture/Pattern/Contrast', 1) - 1, -1, 4),
      rotation: 0,
      blend: textureMode === 1 ? 'subtract' : 'multiply',
      offsetJitter: kritaParamBool(preset, 'Texture/Pattern/isRandomOffsetX', false) || kritaParamBool(preset, 'Texture/Pattern/isRandomOffsetY', false),
    };
    report.implemented.push('pattern texture');
    if (textureMode > 1) report.approximated.push(`texturing mode ${textureMode} rendered as multiply`);
    if (kritaParamNumber(preset, 'Texture/Pattern/CutoffLeft', 0) > 0 || kritaParamNumber(preset, 'Texture/Pattern/CutoffRight', 255) < 255) report.approximated.push('texture cutoff');
    if (strengthOption.enabled && strengthOption.sensors.some((s) => s.id === 'pressure')) report.missing.push('texture strength pressure');
  }

  if (kritaParamBool(preset, 'MaskingBrush/Enabled', false)) {
    report.missing.push({ label: 'masked brush (second tip masking the first)', major: true });
  }
  if (kritaParamBool(preset, 'PaintOpSettings/isAirbrushing', false)) report.missing.push('airbrush rate');

  if (brushType === 'auto_brush' && mask) {
    const id = String(mask.id || 'default').toLowerCase();
    const spikes = Number(mask.spikes) || 2;
    if (!['default', 'soft', 'gauss'].includes(id)) report.approximated.push(`mask generator "${id}"`);
    if (spikes > 2) report.missing.push(`${spikes}-spike mask shape`);
    if (Number(brush.randomness) > 0) report.missing.push('auto brush randomness');
    if (Number(brush.density) > 0 && Number(brush.density) < 1) report.missing.push('auto brush density');
  }
  const brushApplication = Math.round(Number(brush.brushApplication));
  const preserveLightness = String(brush.preserveLightness || '') === '1' || String(brush.preserveLightness || '').toLowerCase() === 'true';
  let tipColour = 'mask';
  if (brushType !== 'auto_brush') {
    if (brushApplication === 1) {
      tipColour = 'image';
      report.implemented.push('colour image stamp');
    } else if (brushApplication === 2 || brushApplication === 3 || preserveLightness) {
      report.approximated.push(brushApplication === 3 ? 'gradient map tip rendered as mask' : 'lightness map tip rendered as mask');
    } else if (!('brushApplication' in brush) && String(brush.ColorAsMask) === '0') {
      tipColour = 'image';
    }
  }

  if (!renderable) report.missing.push({ label: 'brush tip type ' + brushType, major: true });

  return {
    engine: { id: renderable ? 'stamp' : 'unsupported', params: { paintop, kritaScale: Number(brush.scale) || 1 } },
    sourceEngine: 'krita.' + paintop,
    engineLabel,
    brush,
    mask,
    brushType,
    tipColour,
    settings: {
      spacing,
      // Krita's predefined-tip `scale` is the stored size relative to the tip
      // image, not a multiplier on the user's size; it is kept in engine params.
      tipScale: 1,
      angle,
      roundness: 1,
      opacity,
      flow,
      opacityMode: wash ? 'stroke' : 'dab',
      eraser,
      edgeMode,
      tipColour,
      suggestedSize: brushType === 'auto_brush' && mask ? clamp(Number(mask.diameter) || 0, 0, 4000) : 0,
    },
    dynamics: {
      size: sizeChannel,
      opacity: opacityChannel,
      flow: flowChannel,
      spacing: { pressure: spacingChannel.pressure },
      rotation,
      scatter,
      count: { value: 1, jitter: 0 },
      taper: { start: 0, end: 0, size: 0, opacity: 0, pressure: 0 },
      colour,
    },
    texture,
    rendering: { mode: wash ? 'glaze' : 'buildup', wetEdges: 0, burntEdges: 0, blendMode: eraser ? 'erase' : 'normal' },
    compatibility: scoreCompatibility({ ...report, unrenderable: !renderable }),
  };
}
